import React from "react";
import PageWrapper from "../components/PageWrapper";
import "./css/Contact.css";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | GTAFanPage.com</title>
        <meta
          name="description"
          content="Learn how GTAFanPage.com collects, uses, stores, and protects your personal information, including shared account services across affiliated platforms."
        />
        <link
          rel="canonical"
          href="https://www.gtafanpage.com/privacy-policy"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy | GTAFanPage.com" />
        <meta
          property="og:description"
          content="Review GTAFanPage.com's privacy practices, data collection methods, shared services, and user rights."
        />
        <meta
          property="og:url"
          content="https://www.gtafanpage.com/privacy-policy"
        />
        <meta
          property="og:image"
          content="https://www.gtafanpage.com/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | GTAFanPage.com" />
        <meta
          name="twitter:description"
          content="Understand how GTAFanPage.com handles personal data, cookies, analytics, and shared account services."
        />
        <meta
          name="twitter:image"
          content="https://www.gtafanpage.com/og-image.jpg"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Privacy Policy",
            url: "https://www.gtafanpage.com/privacy-policy",
            description:
              "Privacy policy detailing how GTAFanPage.com collects, uses, and protects personal data, including shared services across affiliated platforms.",
            publisher: {
              "@type": "Organization",
              name: "GTAFanPage",
              url: "https://www.gtafanpage.com",
              logo: {
                "@type": "ImageObject",
                url: "https://www.gtafanpage.com/logo192.png",
              },
            },
          })}
        </script>
      </Helmet>

      <PageWrapper>
        <div className="contact-page-wrapper">
          <div className="contact-card about-card">
            <h1>Privacy Policy</h1>
            <p className="about-subtitle">
              Last updated: <strong>January 2026</strong>
            </p>

            {/* 1 */}
            <section className="about-section">
              <h3>1. Introduction</h3>
              <p>
                GTAFanPage.com respects your privacy and is committed to
                protecting personal information provided by users. This Privacy
                Policy explains how we collect, use, store, and safeguard your
                data when you access or interact with our website.
              </p>
            </section>

            {/* 2 */}
            <section className="about-section">
              <h3>2. Information We Collect</h3>
              <p>
                We may collect the following types of information:
              </p>
              <ul className="about-list">
                <li>
                  Personal information such as username, email address, and
                  profile details when you create an account
                </li>
                <li>
                  Technical information including IP address, browser type,
                  device data, and usage statistics
                </li>
                <li>
                  User-generated content such as comments or community posts
                </li>
              </ul>
            </section>

            {/* 3 */}
            <section className="about-section">
              <h3>3. How We Use Your Information</h3>
              <p>
                Information collected by GTAFanPage.com is used to:
              </p>
              <ul className="about-list">
                <li>Provide and maintain site functionality</li>
                <li>Enable user accounts, authentication, and profiles</li>
                <li>Improve content quality and user experience</li>
                <li>Monitor site performance and security</li>
                <li>Communicate important updates or service notices</li>
              </ul>
            </section>

            {/* 4 */}
            <section className="about-section">
              <h3>4. Shared Services Across Affiliated Platforms</h3>
              <p>
                GTAFanPage.com operates as part of an independent fan media
                network. To support shared login functionality and account
                continuity, certain user information may be shared internally
                with affiliated platforms, including <strong>RDRFan.com</strong>.
              </p>
              <p>
                Shared information may include account credentials (securely
                stored), profile details, and user-generated content where
                applicable. This data sharing is limited to internal operational
                purposes and is not intended for commercial resale.
              </p>
            </section>

            {/* 5 */}
            <section className="about-section">
              <h3>5. Cookies & Tracking Technologies</h3>
              <p>
                GTAFanPage.com uses cookies and similar technologies to:
              </p>
              <ul className="about-list">
                <li>Maintain secure sessions and authentication</li>
                <li>Analyze site traffic and usage patterns</li>
                <li>Improve website performance and functionality</li>
                <li>Serve relevant advertisements where applicable</li>
              </ul>
              <p>
                You may control or disable cookies through your browser
                settings; however, some site features may not function properly
                as a result.
              </p>
            </section>

            {/* 6 */}
            <section className="about-section">
              <h3>6. Third-Party Services</h3>
              <p>
                We may use third-party services such as analytics providers and
                advertising partners. These services may collect information in
                accordance with their own privacy policies.
              </p>
              <p>
                GTAFanPage.com does not control third-party data practices and
                encourages users to review applicable external privacy policies.
              </p>
            </section>

            {/* 7 */}
            <section className="about-section">
              <h3>7. Data Security</h3>
              <p>
                We implement reasonable administrative and technical safeguards
                to protect user information from unauthorized access, misuse,
                or disclosure. However, no online system can be guaranteed to be
                completely secure.
              </p>
            </section>

            {/* 8 */}
            <section className="about-section">
              <h3>8. User Rights & Choices</h3>
              <p>
                You have the right to request access to, correction of, or
                deletion of your personal information. Requests may be submitted
                via our Contact page.
              </p>
              <p>
                You may also opt out of certain communications or cookies
                through browser or account settings.
              </p>
            </section>

            {/* 9 */}
            <section className="about-section">
              <h3>9. Children’s Privacy</h3>
              <p>
                GTAFanPage.com does not knowingly collect personal information
                from children under the age of 13. If such data is identified,
                it will be promptly removed.
              </p>
            </section>

            {/* 10 */}
            <section className="about-section">
              <h3>10. Changes to This Privacy Policy</h3>
              <p>
                We may update this Privacy Policy periodically. Changes will be
                posted on this page, and continued use of the website
                constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* 11 */}
            <section className="about-section">
              <h3>11. Contact Information</h3>
              <p>
                For privacy-related questions, data requests, or concerns,
                please visit our{" "}
                <Link to="/contact" className="contact-link">
                  Contact page
                </Link>.
              </p>
            </section>

            <footer className="about-footer">
              <p className="about-tagline">
                <strong>GTAFanPage.com</strong> — Transparent practices,
                responsible data use, and community-first values.
              </p>
            </footer>
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export default Privacy;
